import React, { useState, useRef, useEffect } from 'react';
import { ChevronDown } from 'lucide-react';
import { SidebarMenuButton } from 'components/ui/sidebar';
import { Icon, IconName } from '../menu-icon/menu-icon';
import { cn } from '../../../lib/utils';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from 'components/ui/collapsible';
import { Tooltip, TooltipContent, TooltipTrigger } from '../../ui/tooltip';
import { useTheme } from 'components/core/theme-provider';
import {
    sidebarSectionEntityLabelClass,
    sidebarSectionItemLabelClass,
} from './sidebar-section-label';
import { sidebarNavItemLabelActiveClass } from './sidebar-nav-label';
import { SIDEBAR_NAV_ICON_SIZE, SIDEBAR_NAV_ICON_STROKE, sidebarNavIconClass } from './sidebar-nav-icon';
import {
    sidebarNavItemActiveClassName,
    sidebarSectionItemRowClassName,
} from './sidebar-nav-item-styles';
import { ProjectFolderIcon } from 'components/blocks/layout/project-folder-icon';

// --- Types ---
export interface SidebarRichItemProps {
    title: string;
    subtitle?: string;
    icon?: IconName;
    image?: string;
    isActive?: boolean;
    onClick?: () => void;
    rightAction?: React.ReactNode;
    className?: string;
}

export interface SidebarTreeItemProps {
    label: string;
    icon?: IconName;
    iconElement?: React.ReactNode;
    image?: string;
    folderColor?: string;
    isActive?: boolean;
    onClick?: () => void;
    children?: React.ReactNode; // For nested items
    rightAction?: React.ReactNode;
    defaultOpen?: boolean;
    open?: boolean; // Controlled open state (optional). When provided, overrides internal state.
    onOpenChange?: (open: boolean) => void;
    depth?: number;
    className?: string;
    variant?: 'default' | 'section';
    /** Agent / project name row — Figma Medium 16px. Nested and recent chats stay on the chat label. */
    isEntityName?: boolean;
}


// --- Components ---

/**
 * Rich Item for "My Assistants" or similar.
 * Displays an Icon/Image, a Title, and a Subtitle in a column.
 */
export function SidebarRichItem({
    title,
    subtitle,
    icon,
    image,
    isActive,
    onClick,
    rightAction,
    className,
}: SidebarRichItemProps) {
    const [isHovered, setIsHovered] = useState(false);
    const [isOverflowing, setIsOverflowing] = useState(false);
    const textRef = useRef<HTMLSpanElement>(null);

    useEffect(() => {
        if (!textRef.current) return;
        const checkOverflow = () => {
            if (textRef.current) {
                setIsOverflowing(textRef.current.scrollWidth > textRef.current.clientWidth);
            }
        };

        const observer = new ResizeObserver(checkOverflow);
        observer.observe(textRef.current);
        checkOverflow();

        return () => observer.disconnect();
    }, [title]);

    return (
        <SidebarMenuButton
            onClick={onClick}
            isActive={isActive}
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
            className={cn("h- py-2 group-data-[collapsible=icon]:h-8 group-data-[collapsible=icon]:py-2 relative group/rich-item", className)}
            style={{ paddingLeft: '44px' }}
        >
            <div className="flex min-w-0 flex-1 items-center gap-3 group-data-[collapsible=icon]:justify-center group-data-[collapsible=icon]:px-0 pr-9">
                {/* Helper to render Icon or Image */}
                <div className="shrink-0 flex items-center justify-center">
                    {image ? (
                        <img src={image} alt={title} className="h-5 w-5 object-contain rounded-sm" />
                    ) : icon ? (
                        <Icon name={icon} size={20} className={isActive ? "text-primary" : "text-muted-foreground"} />
                    ) : null}
                </div>

                <div className="flex flex-col min-w-0 text-left group-data-[collapsible=icon]:hidden overflow-hidden">
                    <Tooltip>
                        <TooltipTrigger asChild>
                            <span ref={textRef} className="mb-1 block min-w-0 truncate text-sm font-normal leading-[22px]">{title}</span>
                        </TooltipTrigger>
                        {isOverflowing && (
                            <TooltipContent side="right">
                                {title}
                            </TooltipContent>
                        )}
                    </Tooltip>
                    {subtitle && (
                        <span className="truncate text-xs text-muted-foreground leading-none">
                            {subtitle}
                        </span>
                    )}
                </div>
            </div>

            {rightAction && (
                <div
                    className={cn(
                        "absolute right-2 top-1/2 -translate-y-1/2 transition-opacity group-data-[collapsible=icon]:hidden has-[[data-state=open]]:opacity-100",
                        isHovered || isActive
                            ? "opacity-100"
                            : "opacity-100 lg:opacity-0 lg:group-hover:opacity-100"
                    )}
                    onPointerDown={(e) => e.stopPropagation()}
                    onClick={(e) => e.stopPropagation()}
                >
                    {rightAction}
                </div>
            )}
        </SidebarMenuButton>
    );
}

/**
 * Tree Item for "Projects" or "Chats".
 * Supports nesting via children.
 */
export function SidebarTreeItem({
    label,
    icon,
    iconElement,
    image,
    folderColor,
    isActive,
    onClick,
    children,
    rightAction,
    defaultOpen = false,
    open,
    onOpenChange,
    depth = 0,
    className,
    variant = 'default',
    isEntityName = false,
}: SidebarTreeItemProps) {
    const [internalOpen, setInternalOpen] = useState(defaultOpen);
    const isOpen = open ?? internalOpen;
    const setIsOpen = (next: boolean) => {
        if (onOpenChange) onOpenChange(next);
        else setInternalOpen(next);
    };
    const [isHovered, setIsHovered] = useState(false);
    const [isOverflowing, setIsOverflowing] = useState(false);
    const textRef = useRef<HTMLSpanElement>(null);
    const { theme } = useTheme();
    const isSection = variant === 'section';
    const showFolderIcon = isSection && depth === 0 && (icon === 'Folder' || icon === 'FolderClosed' || icon === 'FolderOpen' || icon === 'FolderDot');
    const sectionLabelColor = theme === 'dark'
        ? 'text-grey-300'
        : isEntityName
            ? 'text-[#1D1D1D]'
            : 'text-[#3D3D3D]';

    useEffect(() => {
        if (!textRef.current) return;
        const checkOverflow = () => {
            if (textRef.current) {
                setIsOverflowing(textRef.current.scrollWidth > textRef.current.clientWidth);
            }
        };

        const observer = new ResizeObserver(checkOverflow);
        observer.observe(textRef.current);
        checkOverflow();

        return () => observer.disconnect();
    }, [label]);

    const hasChildren = React.Children.count(children) > 0;

    // Section Header has px-4 (16px), Icon (16px), Gap-3 (12px). Text starts at 16 + 16 + 12 = 44px.
    // If Tree Item has no icon, it needs 44px padding-left to align label.
    // If it HAS an icon, it needs 16px (px-4) and then 16+12 for the label.
    const basePadding = 44;
    const depthIndent = 0;
    const paddingLeft = basePadding + (depth * depthIndent);

    const renderRightAction = () => {
        if (!rightAction) return null;
        return (
            <div
                className={cn(
                    "absolute right-2 top-1/2 -translate-y-1/2 transition-opacity group-data-[collapsible=icon]:hidden has-[[data-state=open]]:opacity-100",
                    isHovered || isActive
                        ? "opacity-100"
                        : "opacity-100 lg:opacity-0 lg:group-hover:opacity-100"
                )}
                onPointerDown={(e) => e.stopPropagation()}
                onClick={(e) => e.stopPropagation()}
            >
                {rightAction}
            </div>
        );
    };

    const renderSectionIcon = () => {
        if (showFolderIcon) {
            return <ProjectFolderIcon color={folderColor} className="h-5 w-5" />;
        }

        if (iconElement) {
            return iconElement;
        }

        if (image) {
            if (isEntityName) {
                return (
                    <img
                        src={image}
                        alt={label}
                        className="size-[25px] shrink-0 rounded-full object-cover"
                        width={25}
                        height={25}
                    />
                );
            }

            return (
                <img
                    src={image}
                    alt={label}
                    className="size-5 shrink-0 rounded-sm object-contain"
                    width={SIDEBAR_NAV_ICON_SIZE}
                    height={SIDEBAR_NAV_ICON_SIZE}
                />
            );
        }

        if (icon) {
            return (
                <Icon
                    name={icon}
                    size={SIDEBAR_NAV_ICON_SIZE}
                    strokeWidth={SIDEBAR_NAV_ICON_STROKE}
                    className={sidebarNavIconClass}
                />
            );
        }

        return null;
    };

    const sectionLabel = (
        <Tooltip>
            <TooltipTrigger asChild>
                <span
                    ref={textRef}
                    className={cn(
                        isEntityName ? sidebarSectionEntityLabelClass : sidebarSectionItemLabelClass,
                        sectionLabelColor,
                        isActive && sidebarNavItemLabelActiveClass,
                        'block min-w-0 truncate'
                    )}
                >
                    {label}
                </span>
            </TooltipTrigger>
            {isOverflowing && (
                <TooltipContent side="top">{label}</TooltipContent>
            )}
        </Tooltip>
    );

    const sectionRowClass = cn(
        sidebarSectionItemRowClassName,
        rightAction && 'pr-9',
        isActive && sidebarNavItemActiveClassName,
        className
    );

    if (isSection) {
        const sectionContent = (
            <>
                {renderSectionIcon()}
                <div className="min-w-0 flex-1 overflow-hidden group-data-[collapsible=icon]:hidden">{sectionLabel}</div>
                {renderRightAction()}
            </>
        );

        if (!hasChildren) {
            return (
                <button
                    type="button"
                    onClick={onClick}
                    onMouseEnter={() => setIsHovered(true)}
                    onMouseLeave={() => setIsHovered(false)}
                    className={sectionRowClass}
                >
                    {sectionContent}
                </button>
            );
        }

        return (
            <Collapsible open={isOpen} onOpenChange={setIsOpen} className="w-full">
                <CollapsibleTrigger asChild>
                    <button
                        type="button"
                        onMouseEnter={() => setIsHovered(true)}
                        onMouseLeave={() => setIsHovered(false)}
                        className={sectionRowClass}
                        onClick={(e) => {
                            e.stopPropagation();
                            onClick?.();
                        }}
                    >
                        {sectionContent}
                    </button>
                </CollapsibleTrigger>
                <CollapsibleContent className="flex flex-col gap-2">{children}</CollapsibleContent>
            </Collapsible>
        );
    }

    const content = (
        <div className="flex min-w-0 flex-1 items-center gap-3 group-data-[collapsible=icon]:justify-center pr-9">
            {image ? (
                <img src={image} alt={label} className="h-4 w-4 object-contain rounded-sm shrink-0" />
            ) : iconElement ? (
                iconElement
            ) : icon ? (
                <Icon name={icon} size={16} className="shrink-0" />
            ) : null}
            <div className="flex min-w-0 flex-1 items-center gap-1 overflow-hidden group-data-[collapsible=icon]:hidden">
                <Tooltip>
                    <TooltipTrigger asChild>
                        <span ref={textRef} className="block min-w-0 truncate text-sm font-normal leading-[22px]">{label}</span>
                    </TooltipTrigger>
                    {isOverflowing && (
                        <TooltipContent className="text-sm leading-[22px] font-normal" side="bottom">
                            {label}
                        </TooltipContent>
                    )}
                </Tooltip>
                {hasChildren && (
                    <ChevronDown
                        className={cn(
                            "h-4 w-4 transition-transform text-muted-foreground group-data-[collapsible=icon]:hidden",
                            isOpen && "rotate-180"
                        )}
                    />
                )}
            </div>
        </div>
    );

    // Render basic item if no children
    if (!hasChildren) {
        return (
            <SidebarMenuButton
                onClick={onClick}
                isActive={isActive}
                onMouseEnter={() => setIsHovered(true)}
                onMouseLeave={() => setIsHovered(false)}
                className={cn("h-8 relative group/tree-item", className)}
                style={{ paddingLeft: `${paddingLeft}px` }}
            >
                {content}
                {renderRightAction()}
            </SidebarMenuButton>
        );
    }

    // Render collapsible item if has children
    return (
        <Collapsible open={isOpen} onOpenChange={setIsOpen} className="w-full">
            <CollapsibleTrigger asChild>
                <SidebarMenuButton
                    isActive={isActive}
                    onMouseEnter={() => setIsHovered(true)}
                    onMouseLeave={() => setIsHovered(false)}
                    className={cn("h-8 group/tree-item relative", className)}
                    style={{ paddingLeft: `${paddingLeft}px` }}
                    onClick={(e) => {
                        e.stopPropagation();
                        if (onClick) onClick();
                    }}
                >
                    {content}
                    {renderRightAction()}
                </SidebarMenuButton>
            </CollapsibleTrigger>
            <CollapsibleContent className="flex flex-col">
                {children}
            </CollapsibleContent>
        </Collapsible>
    );
}
